<script lang="ts" setup>
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Dialog from 'primevue/dialog'
import type { Check } from '~/types/checks'

const { t, locale } = useI18n()
const dialogRef = inject('dialogRef') as any
const checkData = computed(() => dialogRef?.value?.data?.checkData as Check)
const { printOrder } = usePrintWrapper()
const businessStore = useBusinessStore()
const { businessInfo: business } = storeToRefs(businessStore)

const showEmailDialog = ref(false)
const customerEmail = ref('')
const sendingEmail = ref(false)

async function handlePrint() {
  await printOrder(checkData.value)
}

function handleEmail() {
  customerEmail.value = ''
  showEmailDialog.value = true
}

async function sendReceiptEmail() {
  if (!customerEmail.value) {
    useErrorToast('Please enter an email address')
    return
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(customerEmail.value)) {
    useErrorToast('Please enter a valid email address')
    return
  }

  try {
    sendingEmail.value = true
    await $fetch('/api/email/send-receipt', {
      method: 'POST',
      body: {
        email: customerEmail.value,
        checkData: checkData.value,
        business: business.value,
        locale: locale.value,
      },
    })
    useSuccessToast('Email sent successfully')
    showEmailDialog.value = false
    customerEmail.value = ''
  }
  catch (error: any) {
    console.error('Error sending email:', error)
    useErrorToast(error.data?.message || 'Failed to send email')
  }
  finally {
    sendingEmail.value = false
  }
}
</script>

<template>
  <div v-if="checkData">
    <!-- Receipt card -->
    <div class="flex justify-center">
      <CheckReceiptCard :check="checkData" />
    </div>

    <!-- Action Buttons -->
    <div class="flex gap-3 justify-end mt-6 print:hidden">
      <Button
        :label="t('Send Email')"
        icon="pi pi-envelope"
        severity="secondary"
        outlined
        @click="handleEmail"
      />
      <Button
        :label="t('Print')"
        icon="pi pi-print"
        severity="success"
        @click="handlePrint"
      />
    </div>

    <!-- Email Dialog -->
    <Dialog
      v-model:visible="showEmailDialog"
      :header="t('Send Receipt via Email')"
      :modal="true"
      class="w-full max-w-md"
    >
      <div class="flex flex-col gap-4">
        <div>
          <label
            for="customer-email"
            class="block text-sm font-medium text-gray-700 mb-2"
          >
            {{ t('Customer Email') }}
          </label>
          <InputText
            id="customer-email"
            v-model="customerEmail"
            type="email"
            :placeholder="t('Enter email address')"
            class="w-full"
            autofocus
            @keyup.enter="sendReceiptEmail"
          />
        </div>
      </div>

      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button
            :label="t('Cancel')"
            severity="secondary"
            outlined
            @click="showEmailDialog = false"
          />
          <Button
            :label="t('Send')"
            icon="pi pi-send"
            :loading="sendingEmail"
            @click="sendReceiptEmail"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
